import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { useOrganizationStore } from "../../../store/organization";
import { useEventStore } from "../../../store/event";
import { userStore } from "../../../store/user";
import { usePermissions } from "../../auth/api";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import { SecondaryButton } from "../../../components/Button";
import { usdFormat } from "../../../utils";
import {
  ORDER_AHEAD_ORDERS_ALL_QUERY,
  ORDER_AHEAD_ORDERS_COUNT_QUERY,
  ORDER_AHEAD_ORDERS_LIST_QUERY,
  ORDER_AHEAD_ORDER_VENDORS_QUERY,
} from "./queries";
import {
  exportOrderAheadOrdersCsv,
  OrderAheadOrderRow,
} from "./exportOrderAheadOrdersCsv";
import {
  ORDER_AHEAD_STATUS_OPTIONS as STATUS_OPTIONS,
  orderAheadStatusLabel as statusLabel,
} from "./orderAheadOrder";

const client = createApolloClient();

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type SortField =
  | "created_at"
  | "id"
  | "reference_id"
  | "status"
  | "expo_status"
  | "email"
  | "phone"
  | "location"
  | "vendor"
  | "event";
type SortDir = "asc" | "desc";

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case "voided":
      return "inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200";
    case "refund":
      return "inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200";
    default:
      return "";
  }
};

const escapeIlike = (s: string) => s.replace(/[%_\\]/g, (m) => `\\${m}`);

const fmtDate = (iso?: string) =>
  iso ? moment.utc(iso).format("MM/DD/YYYY h:mm a") : "—";

const orderTotal = (o: OrderAheadOrderRow) =>
  (o.subtotal ?? 0) + (o.tax ?? 0) + (o.tip ?? 0) + (o.digital_surcharge ?? 0);

// Total is a computed sum of four columns, so it has no server-sortable field.
const buildOrderBy = (field: SortField, dir: SortDir): any[] => {
  switch (field) {
    case "id":
      return [{ id: dir }];
    case "reference_id":
      return [{ reference_id: dir }];
    case "status":
      return [{ status: dir }];
    case "expo_status":
      return [{ expo_status: dir }];
    case "email":
      return [{ email: dir }];
    case "phone":
      return [{ phone_number: dir }];
    case "location":
      return [{ location: { name: dir } }];
    case "vendor":
      return [{ vendor: { name: dir } }];
    case "event":
      return [{ event: { name: dir } }];
    case "created_at":
    default:
      return [{ created_at: dir }];
  }
};

const columns: {
  label: string;
  field: SortField | null;
  numeric?: boolean;
  hideOn?: "sm" | "md" | "lg";
}[] = [
  { label: "Date", field: "created_at" },
  { label: "Order ID", field: "id", numeric: true },
  { label: "Reference ID", field: "reference_id" },
  { label: "Total", field: null, numeric: true },
  { label: "Status", field: "status" },
  { label: "Expo Status", field: "expo_status" },
  { label: "Email", field: "email", hideOn: "sm" },
  { label: "Phone", field: "phone", hideOn: "lg" },
  { label: "Location", field: "location", hideOn: "md" },
  { label: "Vendor", field: "vendor", hideOn: "md" },
  { label: "Event", field: "event", hideOn: "lg" },
];

const hideClass = (hideOn?: "sm" | "md" | "lg") => {
  switch (hideOn) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    default:
      return "";
  }
};

interface VendorOption {
  id: number;
  name: string;
}

export const OrderAheadOrdersList = () => {
  const { SearchPermission } = usePermissions("ticketingReporting");
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState<OrderAheadOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const [query, setQuery] = useState(
    () => new URLSearchParams(location.search).get("search") ?? ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [vendorId, setVendorId] = useState<number | null>(() => {
    const v = new URLSearchParams(location.search).get("vendor");
    const n = v ? parseInt(v, 10) : NaN;
    return Number.isNaN(n) ? null : n;
  });
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [status, setStatus] = useState<string | null>(() => {
    const s = new URLSearchParams(location.search).get("status");
    return STATUS_OPTIONS.some((o) => o.value === s) ? s : null;
  });
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, vendorId, status, sortField, sortDir, headerOrgId, headerEventId]);

  // Org + event scope only — drives both the vendor dropdown and the list filter.
  const scopeWhere = useMemo(() => {
    const organizationId = parseInt(
      headerOrgId !== null ? headerOrgId.toString() : "",
      10
    );
    const eventId = headerEventId;
    const user = userStore.getState().user;

    const w: any = {};
    if (organizationId && !Number.isNaN(organizationId) && organizationId !== 0) {
      w.event = { organization_id: { _eq: organizationId } };
    }
    if (eventId) {
      w.event_id = { _eq: eventId };
    } else if (user?.events?.length) {
      w.event_id = { _in: user.events };
    }
    return w;
  }, [headerOrgId, headerEventId]);

  const where = useMemo(() => {
    const w: any = { ...scopeWhere };
    if (vendorId != null) {
      w.vendor_id = { _eq: vendorId };
    }
    if (status != null) {
      w.status = { _eq: status };
    }
    const q = debouncedQuery.trim();
    if (q) {
      const like = `%${escapeIlike(q)}%`;
      w._or = [
        { email: { _ilike: like } },
        { phone_number: { _ilike: like } },
        { reference_id: { _ilike: like } },
      ];
    }
    return w;
  }, [scopeWhere, vendorId, status, debouncedQuery]);

  const orderBy = useMemo(() => buildOrderBy(sortField, sortDir), [sortField, sortDir]);

  const fetchPage = useCallback(async () => {
    setIsLoading(true);
    try {
      const [{ data: pageData }, { data: countData }] = await Promise.all([
        client.query({
          query: ORDER_AHEAD_ORDERS_LIST_QUERY,
          variables: { where, order_by: orderBy, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: ORDER_AHEAD_ORDERS_COUNT_QUERY,
          variables: { where },
          fetchPolicy: "network-only",
        }),
      ]);
      if (!mountedRef.current) return;
      setOrders(pageData?.orders_order_ahead ?? []);
      setTotal(countData?.orders_order_ahead_aggregate?.aggregate?.count ?? 0);
    } catch (error) {
      console.error("Error fetching order-ahead orders:", error);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [where, orderBy, page]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Load the vendor filter options for the current event/org scope.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.query({
          query: ORDER_AHEAD_ORDER_VENDORS_QUERY,
          variables: { where: scopeWhere },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const opts: VendorOption[] = (data?.orders_order_ahead ?? [])
          .map((r: any) => r.vendor)
          .filter((v: any): v is VendorOption => v && typeof v.id === "number")
          .sort((a: VendorOption, b: VendorOption) => a.name.localeCompare(b.name));
        setVendorOptions(opts);
      } catch (error) {
        console.error("Error loading vendor filter options:", error);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeWhere]);

  // Drop a selected vendor that no longer belongs to the current scope.
  useEffect(() => {
    if (vendorId != null && !vendorOptions.some((v) => v.id === vendorId)) {
      setVendorId(null);
    }
  }, [vendorOptions, vendorId]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("search", query);
    if (vendorId != null) params.set("vendor", String(vendorId));
    if (status != null) params.set("status", status);
    const next = params.toString();
    const current = location.search.replace(/^\?/, "");
    if (next !== current) {
      navigate(next ? `/order-ahead-orders/list?${next}` : "/order-ahead-orders/list", {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, vendorId, status]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const { data } = await client.query({
        query: ORDER_AHEAD_ORDERS_ALL_QUERY,
        variables: { where, order_by: orderBy },
        fetchPolicy: "network-only",
      });
      exportOrderAheadOrdersCsv((data?.orders_order_ahead ?? []) as OrderAheadOrderRow[]);
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.error(error?.message ?? "Failed to export orders");
    } finally {
      if (mountedRef.current) setIsExporting(false);
    }
  }, [where, orderBy]);

  const onSortChange = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir(field === "id" || field === "created_at" ? "desc" : "asc");
      }
    },
    [sortField]
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Breadcrumbs />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Vendor
              </label>
              <select
                value={vendorId ?? ""}
                onChange={(e) =>
                  setVendorId(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                className="pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All vendors</option>
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Status
              </label>
              <select
                value={status ?? ""}
                onChange={(e) => setStatus(e.target.value || null)}
                className="pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <SecondaryButton onClick={handleExport} isDisabled={isExporting || total === 0}>
              {isExporting ? "Exporting…" : "Export to CSV"}
            </SecondaryButton>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">Order Ahead Orders</div>
            <div className="text-xs text-gray-500">
              {total === 0
                ? "0 orders"
                : `${startRow.toLocaleString()}–${endRow.toLocaleString()} of ${total.toLocaleString()}`}
            </div>
          </div>
          <div className="relative w-48 sm:w-64 flex-shrink-0">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email, phone, reference…"
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
            />
            <svg
              className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
          </div>
        </div>

        <div className="overflow-x-auto relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
              <div className="text-xs text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm">
                Loading…
              </div>
            </div>
          )}
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-gray-200">
                {columns.map((col) => (
                  <th
                    key={col.label}
                    onClick={() => col.field && onSortChange(col.field)}
                    className={`px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold select-none ${
                      col.field ? "cursor-pointer hover:text-gray-800" : ""
                    } ${col.numeric ? "text-right" : "text-left"} ${hideClass(col.hideOn)}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.field && <SortIcon active={sortField === col.field} dir={sortDir} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.length === 0 && !isLoading && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-gray-400 text-sm"
                  >
                    {query ? `No orders match "${query}".` : "No orders to display."}
                  </td>
                </tr>
              )}
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={SearchPermission ? () => navigate(`/order-ahead-orders/${o.id}`) : undefined}
                  className={`transition-colors ${
                    SearchPermission ? "cursor-pointer hover:bg-gray-50" : ""
                  }`}
                >
                  <td className="px-3 py-2.5 align-middle text-gray-700 whitespace-nowrap">
                    {fmtDate(o.created_at)}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-right tabular-nums font-medium text-gray-900">
                    {o.id}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-gray-700">
                    {o.reference_id || "—"}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-right tabular-nums font-medium text-gray-900">
                    {usdFormat.format(orderTotal(o) / 100)}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-gray-700 whitespace-nowrap">
                    {statusBadgeClass(o.status) ? (
                      <span className={statusBadgeClass(o.status)}>{statusLabel(o.status)}</span>
                    ) : (
                      statusLabel(o.status)
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-gray-700">
                    {o.expo_status || "—"}
                  </td>
                  <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("sm")}`}>
                    {o.email || "—"}
                  </td>
                  <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("lg")}`}>
                    {o.phone_number || "—"}
                  </td>
                  <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("md")}`}>
                    {o.location?.name || "—"}
                  </td>
                  <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("md")}`}>
                    {o.vendor?.name || "—"}
                  </td>
                  <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("lg")}`}>
                    {o.event?.name || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50">
          <div className="text-xs text-gray-500">
            {total === 0 ? "0 results" : `Page ${page + 1} of ${totalPages.toLocaleString()}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isLoading}
              className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages || isLoading}
              className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => (
  <svg
    className={`w-3 h-3 ${active ? "text-gray-700" : "text-gray-300"}`}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    {dir === "asc" && active ? (
      <path d="M5 13l5-5 5 5H5z" />
    ) : dir === "desc" && active ? (
      <path d="M5 7l5 5 5-5H5z" />
    ) : (
      <>
        <path d="M5 9l5-5 5 5H5z" />
        <path d="M5 11l5 5 5-5H5z" opacity="0.5" />
      </>
    )}
  </svg>
);
